import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Header from "../components/header/Header";
import MobileNavBar from "../components/home/MobileNavBar";
import Loading from "../components/common/Loading";
import { useFetchUser } from "../hooks/user/useFetchUser";

/**
 * MainLayout - A consistent layout wrapper for all authenticated pages
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - The page content to render
 * @param {boolean} props.noContainer - If true, don't wrap content in a container (for full-width pages)
 * @param {boolean} props.noPadding - If true, don't add default padding to the content
 * @param {string} props.className - Additional classes to add to the main content wrapper
 */
const MainLayout = ({
  children,
  noContainer = false,
  noPadding = false,
  className = "",
}) => {
  const { isLoading: isAuthLoading } = useAuth0();
  const { data: dbUser, isLoading: isUserLoading } = useFetchUser();

  const isLoading = isAuthLoading || isUserLoading;

  if (isLoading) {
    return <Loading />;
  }

  // Set up default classes for the main content area with proper spacing
  // Header clearance: pt-24 for mobile, pt-24 for desktop
  // Content padding: add py-6 md:py-12 unless noPadding is true
  // Mobile nav clearance: ensure pb-20 minimum for mobile (h-16 nav + 1rem)
  const headerPadding = "pt-24 md:pt-32";
  const contentPadding = noPadding ? "" : "py-6 md:py-12";
  const mobilePadding = "pb-20 md:pb-0"; // Override bottom padding on mobile for nav clearance
  const mainClasses = `flex-grow ${headerPadding} ${contentPadding} ${mobilePadding} ${className}`;

  return (
    <div className="flex flex-col min-h-screen bg-light-grey">
      <Header user={dbUser} />

      {noContainer ? (
        <main className={mainClasses}>{children}</main>
      ) : (
        <main className={mainClasses}>
          <div className="container mx-auto px-5 mb-10 md:mb-0">
            <div className="max-w-7xl mx-auto">{children}</div>
          </div>
        </main>
      )}

      <MobileNavBar />
    </div>
  );
};

export default MainLayout;
